import React, { useState, useEffect, useRef } from "react";
import "./EpicGamingHub.scss";

/**
 * EPIC GAMING HUB - Where visual storytelling meets gaming
 * Rich, immersive, cinematic - a true gaming experience
 */
const EpicGamingHub: React.FC = () => {
    const [activeQuest, setActiveQuest] = useState(0);
    const [hoveredCard, setHoveredCard] = useState<number | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Dynamic particle system for atmosphere
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const particles: Array<{
            x: number;
            y: number;
            vx: number;
            vy: number;
            size: number;
            color: string;
            life: number;
        }> = [];

        const colors = ["#7859E6", "#FF4238", "#00D4FF", "#FFD700"];

        const createParticle = () => {
            particles.push({
                x: Math.random() * canvas.width,
                y: canvas.height + 10,
                vx: (Math.random() - 0.5) * 2,
                vy: -Math.random() * 3 - 1,
                size: Math.random() * 3 + 1,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 1,
            });
        };

        const animate = () => {
            ctx.fillStyle = "rgba(0, 0, 0, 0.05)";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            if (Math.random() < 0.1) createParticle();

            particles.forEach((particle, index) => {
                particle.x += particle.vx;
                particle.y += particle.vy;
                particle.life -= 0.01;

                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                ctx.fillStyle = particle.color;
                ctx.globalAlpha = particle.life;
                ctx.fill();
                ctx.globalAlpha = 1;

                if (particle.life <= 0 || particle.y < -10) {
                    particles.splice(index, 1);
                }
            });

            requestAnimationFrame(animate);
        };

        animate();
    }, []);

    const epicQuests = [
        {
            id: 1,
            brand: "MONSTER ENERGY",
            title: "CLUTCH OR KICK TOURNAMENT",
            image: "https://cdn.devourgo.io/quests/monster-tournament.jpg",
            video: "https://cdn.devourgo.io/videos/monster-epic.mp4",
            color: "#76FF03",
            prize: "$25,000 PRIZE POOL",
            deadline: "FINALS IN 48H",
            participants: "12.4K",
            type: "TOURNAMENT",
            featured: true,
        },
        {
            id: 2,
            brand: "RAZER",
            title: "RGB RAMPAGE",
            image: "https://cdn.devourgo.io/quests/razer-rgb.jpg",
            color: "#00FF00",
            prize: "CUSTOM GAMING SETUP",
            deadline: "5 DAYS LEFT",
            participants: "8.2K",
            type: "CHALLENGE",
        },
        {
            id: 3,
            brand: "G FUEL",
            title: "ENERGY OVERDRIVE",
            image: "https://cdn.devourgo.io/quests/gfuel-overdrive.jpg",
            color: "#FF006E",
            prize: "YEAR SUPPLY + MERCH",
            deadline: "72 HOURS",
            participants: "15.7K",
            type: "QUEST",
        },
    ];

    const currentQuest = epicQuests[activeQuest];

    return (
        <div className="epic-gaming-hub">
            <canvas ref={canvasRef} className="atmosphere-canvas" />

            {/* CINEMATIC BACKGROUND */}
            <div className="cinematic-bg">
                <video
                    ref={videoRef}
                    className="bg-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                >
                    <source src={currentQuest.video || "/videos/epic-gaming-bg.mp4"} type="video/mp4" />
                </video>
                <div className="bg-overlay" />
                <div className="bg-gradient" style={{
                    background: `radial-gradient(circle at center, transparent 0%, ${currentQuest.color}22 50%, #000 100%)`,
                }} />
            </div>

            {/* MAIN STAGE - HERO QUEST */}
            <div className="main-stage">
                <div className="quest-spotlight">
                    <div className="spotlight-badge">{currentQuest.type}</div>

                    <div className="quest-hero-content">
                        <div className="brand-presenter">
                            <img src={`/brands/${currentQuest.brand.toLowerCase().replace(" ", "-")}.png`} alt={currentQuest.brand} />
                            <span className="presents">PRESENTS</span>
                        </div>

                        <h1 className="quest-mega-title" style={{ textShadow: `0 0 40px ${currentQuest.color}` }}>
                            {currentQuest.title}
                        </h1>

                        <div className="quest-hero-stats">
                            <div className="hero-stat">
                                <span className="stat-icon">🏆</span>
                                <span className="stat-value">{currentQuest.prize}</span>
                            </div>
                            <div className="hero-stat">
                                <span className="stat-icon">⏱️</span>
                                <span className="stat-value">{currentQuest.deadline}</span>
                            </div>
                            <div className="hero-stat">
                                <span className="stat-icon">👥</span>
                                <span className="stat-value">{currentQuest.participants} PLAYING</span>
                            </div>
                        </div>

                        <button className="mega-cta" style={{
                            background: `linear-gradient(135deg, ${currentQuest.color} 0%, ${currentQuest.color}88 100%)`,
                            boxShadow: `0 10px 40px ${currentQuest.color}66`,
                        }}>
                            <span className="cta-text">ENTER NOW</span>
                            <div className="cta-energy" />
                        </button>
                    </div>

                    {/* 3D QUEST CAROUSEL */}
                    <div className="quest-carousel">
                        {epicQuests.map((quest, index) =>
                            <div
                                key={quest.id}
                                className={`carousel-card ${index === activeQuest ? "active" : ""}`}
                                style={{
                                    "--index": index,
                                    "--offset": index - activeQuest,
                                    backgroundImage: `url(${quest.image})`,
                                } as any}
                                onClick={() => setActiveQuest(index)}
                                onMouseEnter={() => setHoveredCard(index)}
                                onMouseLeave={() => setHoveredCard(null)}
                            >
                                <div className="card-glow" style={{ background: quest.color }} />
                                <div className="card-content">
                                    <span className="card-brand">{quest.brand}</span>
                                    <h3 className="card-title">{quest.title}</h3>
                                </div>
                                {hoveredCard === index &&
                  <div className="card-preview">
                      <video autoPlay muted loop>
                          <source src={quest.video} type="video/mp4" />
                      </video>
                  </div>
                                }
                            </div>)}
                    </div>
                </div>
            </div>

            {/* IMMERSIVE SIDEBAR */}
            <aside className="immersive-sidebar">
                <div className="sidebar-glow" />

                {/* PLAYER CARD */}
                <div className="player-card-3d">
                    <div className="card-layer layer-bg" />
                    <div className="card-layer layer-pattern" />
                    <div className="card-layer layer-content">
                        <div className="player-avatar-hologram">
                            <div className="hologram-ring ring-1" />
                            <div className="hologram-ring ring-2" />
                            <div className="hologram-ring ring-3" />
                            <img src="/placeholder-avatar.jpg" alt="Player" />
                            <div className="level-orb">42</div>
                        </div>
                        <h3 className="player-name">DEATHKILLER99</h3>
                        <div className="player-title">APEX PREDATOR</div>

                        <div className="stats-hologram">
                            <div className="stat-prism">
                                <span className="prism-value">2.4K</span>
                                <span className="prism-label">RANK</span>
                            </div>
                            <div className="stat-prism">
                                <span className="prism-value">78%</span>
                                <span className="prism-label">WIN</span>
                            </div>
                            <div className="stat-prism">
                                <span className="prism-value">1.8</span>
                                <span className="prism-label">K/D</span>
                            </div>
                        </div>
                    </div>
                    <div className="card-layer layer-shine" />
                </div>

                {/* LIVE FEED */}
                <div className="live-feed-container">
                    <div className="feed-header">
                        <span className="live-dot" />
                        <span className="feed-title">LIVE FEED</span>
                    </div>

                    <div className="feed-stream">
                        <div className="feed-event critical">
                            <div className="event-icon">🔥</div>
                            <div className="event-content">
                                <span className="event-player">xNinja</span> just won
                                <span className="event-highlight"> $5,000</span> in Monster Tournament!
                            </div>
                            <div className="event-time">NOW</div>
                        </div>

                        <div className="feed-event epic">
                            <div className="event-icon">💎</div>
                            <div className="event-content">
                                <span className="event-highlight">LEGENDARY LOOTBOX</span> opened by Phoenix
                            </div>
                            <div className="event-time">2m</div>
                        </div>

                        <div className="feed-event">
                            <div className="event-icon">⚔️</div>
                            <div className="event-content">
                                Guild <span className="event-highlight">FaZe Clan</span> started Guild War
                            </div>
                            <div className="event-time">5m</div>
                        </div>
                    </div>
                </div>

                {/* QUICK ACTIONS */}
                <div className="quick-actions">
                    <button className="action-orb">
                        <div className="orb-bg" />
                        <span className="orb-icon">🎮</span>
                        <span className="orb-label">PLAY</span>
                    </button>
                    <button className="action-orb">
                        <div className="orb-bg" />
                        <span className="orb-icon">🛍️</span>
                        <span className="orb-label">SHOP</span>
                    </button>
                    <button className="action-orb">
                        <div className="orb-bg" />
                        <span className="orb-icon">👥</span>
                        <span className="orb-label">GUILD</span>
                    </button>
                </div>
            </aside>

            {/* BOTTOM DOCK */}
            <div className="bottom-dock">
                <div className="dock-blur" />

                <div className="dock-content">
                    {/* MINI MAP */}
                    <div className="mini-map">
                        <div className="map-container">
                            <div className="map-grid" />
                            <div className="map-point you" style={{ left: "45%", top: "60%" }}>
                                <span className="point-label">YOU</span>
                            </div>
                            <div className="map-point objective" style={{ left: "70%", top: "30%" }}>
                                <span className="point-label">QUEST</span>
                            </div>
                            <div className="map-point squad" style={{ left: "30%", top: "45%" }}>
                                <span className="point-label">SQUAD</span>
                            </div>
                        </div>
                    </div>

                    {/* ACTIVE QUESTS BAR */}
                    <div className="active-quests-bar">
                        <div className="quest-slot active">
                            <div className="slot-bg" />
                            <img src="/quests/quest-icon-1.png" alt="Quest" />
                            <div className="slot-timer">23:47</div>
                            <div className="slot-progress">
                                <div className="progress-fill" style={{ width: "67%" }} />
                            </div>
                        </div>
                        <div className="quest-slot">
                            <div className="slot-bg" />
                            <img src="/quests/quest-icon-2.png" alt="Quest" />
                            <div className="slot-progress">
                                <div className="progress-fill" style={{ width: "30%" }} />
                            </div>
                        </div>
                        <div className="quest-slot">
                            <div className="slot-bg" />
                            <img src="/quests/quest-icon-3.png" alt="Quest" />
                            <div className="slot-progress">
                                <div className="progress-fill" style={{ width: "90%" }} />
                            </div>
                        </div>
                        <div className="quest-slot empty">
                            <div className="slot-bg" />
                            <span className="slot-add">+</span>
                        </div>
                    </div>

                    {/* RESOURCES */}
                    <div className="resource-display">
                        <div className="resource fuel">
                            <div className="resource-icon">⚡</div>
                            <div className="resource-value">2,847</div>
                            <div className="resource-bar">
                                <div className="bar-fill" style={{ width: "78%" }} />
                            </div>
                        </div>
                        <div className="resource gems">
                            <div className="resource-icon">💎</div>
                            <div className="resource-value">12</div>
                            <button className="resource-add">+</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* FLOATING UI ELEMENTS */}
            <div className="floating-elements">
                <div className="notification-orb">
                    <span className="orb-count">3</span>
                    <div className="orb-pulse" />
                </div>

                <div className="combo-counter">
                    <span className="combo-label">COMBO</span>
                    <span className="combo-value">x12</span>
                    <div className="combo-bar">
                        <div className="bar-energy" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EpicGamingHub;